import { useEffect, useState } from "react";
import type { TFunction } from "i18next";
import { type IResourceItem } from "@refinedev/core";
import { EVENTO_PERFIL, authProvider, perfilEnCache } from "@/services/auth";

import {
  ShopOutlined,
  ContactsOutlined,
  DashboardOutlined,
  DotChartOutlined,
  MessageOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import { BoxCloseIcon, PresupuestoIcon } from "@/components";
import { SellerIcon } from "@/components/icon";

/**
 * ¿Entra al Catálogo?
 *
 * El permiso es **global**: no cuelga de un store ni de un seller, así que vive en el
 * tercer bucket, en `permissions.global.catalog.product`. Antes se leía de `store` y
 * `seller` — quedó ahí cuando el catálogo se movió a global, y el menú dejaba de
 * mostrarse para todos.
 *
 * **`by_relation` SÍ entra**, y es la diferencia con el resto del admin. En Presupuestos
 * o Pedidos significa "sólo los registros donde participás", así que no alcanza para una
 * sección entera. En el Catálogo la visibilidad es todo o nada —es lo que sostiene el
 * catálogo unificado— y `by_relation` recorta **qué se edita**, no qué se ve: ve los
 * 44.509 productos igual que un `viewer`.
 *
 * El default es `no_access` (falla cerrado), así que sin fila no se ve la sección.
 */
const tieneCatalogo = (identity: any): boolean => {
  const nivel = String(
    identity?.permissions?.global?.catalog?.product ?? "",
  )
    .trim()
    .toLowerCase();

  return nivel !== "" && nivel !== "no_access";
};

/**
 * El perfil con el que se arma el menú.
 *
 * **No usa `useGetIdentity` de Refine, y no se puede.** `Resources()` se invoca desde el
 * render de `App` para pasárselo a `<Refine resources=…>`, o sea **fuera** del provider: sus
 * hooks corren en el contexto de `App`, donde no hay QueryClient. `useGetIdentity` lo pide, y
 * el resultado era `No QueryClient set` — una excepción sin error boundary, o sea la
 * aplicación entera en blanco.
 *
 * Va contra el `authProvider` directo, que es la misma fuente que Refine usa por debajo y
 * además cachea el perfil a nivel de módulo: es una sola llamada por sesión.
 */
export const usePerfilDelMenu = () => {
  /**
   * Arranca con el perfil que ya está en cache —del login o de la carga anterior— para que
   * el menú se dibuje bien **en el primer render**. Refine toma `resources` al montar, así
   * que un perfil que llega 50 ms después puede no rearmar el menú: por eso no alcanza con
   * esperar el fetch.
   */
  const [identity, setIdentity] = useState<any>(() => perfilEnCache());

  useEffect(() => {
    let vivo = true;
    const pedir = () =>
      authProvider
        .getIdentity?.()
        // Sin perfil el menú se arma igual, sin las secciones que dependen de un permiso:
        // falla cerrado, que es el default de `catalog`.
        .then((perfil) => vivo && setIdentity(perfil))
        .catch(() => undefined);

    pedir();

    /**
     * **El perfil cambia sin que la app se vuelva a montar**: cerrar sesión y entrar es
     * navegación, no recarga. Sin escuchar el aviso, el menú quedaba con el perfil del
     * arranque y un permiso recién dado no aparecía hasta un Ctrl+Shift+R. Si el aviso
     * llega sin perfil en cache (recién logueado), se pide.
     */
    const alCambiar = () => {
      const enCache = perfilEnCache();
      setIdentity(enCache);
      if (!enCache && localStorage.getItem("access_token")) pedir();
    };
    window.addEventListener(EVENTO_PERFIL, alCambiar);

    return () => {
      vivo = false;
      window.removeEventListener(EVENTO_PERFIL, alCambiar);
    };
  }, []);

  return identity;
};

/**
 * El árbol del menú. Recibe `t` y el perfil en vez de tomarlos de un hook, por lo de arriba.
 */
export const Resources = (t: TFunction, identity?: any) => {
  const resources: IResourceItem[] = [
    {
      name: "/",
      list: "/",
      meta: {
        key: "/",
        label: t("menu.label.home"),
        icon: <DashboardOutlined />,
      },
    },
    {
      name: "sales_management",
      meta: {
        label: t("menu.label.salesManagement"),
        type: "group",
      },
    },
    {
      name: "contacts",
      list: "/contacts",
      meta: {
        key: "contacts",
        label: t("menu.label.contacts"),
        parent: "sales_management",
        icon: <ContactsOutlined />,
      },
    },
    {
      name: "conversations",
      list: "/conversations",
      meta: {
        key: "conversations",
        label: t("menu.label.conversations"),
        parent: "sales_management",
        icon: <MessageOutlined />,
      },
    },
    {
      name: "quotes",
      list: "/quotes",
      meta: {
        key: "quotes",
        label: t("menu.label.quotes"),
        parent: "sales_management",
        icon: <PresupuestoIcon />,
      },
    },
    {
      name: "orders",
      list: "/orders",
      meta: {
        key: "orders",
        label: t("menu.label.orders"),
        parent: "sales_management",
        icon: (
          <BoxCloseIcon
            style={{ marginRight: "-6px", position: "relative", right: 3 }}
          />
        ),
      },
    },
    // {
    //   name: "reports",
    //   list: "/reports",
    //   meta: {
    //     key: "reports",
    //     label: t("menu.label.reports"),
    //     parent: "sales_management",
    //     icon: <DotChartOutlined />,
    //   },
    // },
    {
      name: "administrator",
      meta: {
        label: t("menu.label.administration"),
        type: "group",
      },
    },
    {
      name: "stores",
      list: "/stores",
      create: "/stores/create",
      edit: "/stores/edit/:id",
      show: "/stores/:id",
      meta: {
        key: "stores",
        label: t("menu.label.stores"),
        parent: "administrator",
        icon: <ShopOutlined />,
      },
    },
    {
      name: "sellers",
      list: "/sellers",
      create: "/sellers/create",
      edit: "/sellers/edit/:id",
      show: "/sellers/:id",
      meta: {
        key: "sellers",
        label: t("menu.label.sellers"),
        parent: "administrator",
        icon: <SellerIcon />,
        smartMenu: true,
      },
    },
  ];

  // El catálogo es global: no lleva scope de store en la ruta, así que su gate es el
  // permiso `catalog` en cualquiera de los scopes del operador.
  //
  // **Se saca del array en vez de esconderse con `CanAccess`** porque este admin no tiene
  // `accessControlProvider`: `CanAccess` deja pasar todo, y las otras secciones se apoyan
  // en que el backend conteste 403. Acá el requerimiento es que la sección **no se
  // muestre** (F2), así que el gate va donde se arma el menú.
  if (tieneCatalogo(identity)) {
    resources.push({
      name: "catalog",
      list: "/catalog",
      show: "/catalog/:sku",
      meta: {
        key: "catalog",
        label: t("menu.label.catalog"),
        parent: "administrator",
        icon: <AppstoreOutlined />,
      },
    });
  }

  return resources;
};
